import { FC, useEffect, useState } from 'react';
import {
  CourseRecord,
  DownloadStateTitle,
  DownloadStatus,
  ITraining,
  TrainingRecord,
} from '@taleemabad/db';
import {
  ChipAssist,
  IconText,
  Spacing,
  StyledDiv,
  TrainingCardList,
  Typography,
} from '@taleemabad/dsm/components';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { Capacitor } from '@capacitor/core';
import { ChipFilterWithContainer } from '../../../atoms/chip/chip-filter';

interface CourseTrainingsProps {
  course: CourseRecord;
  courseCategoryName?: string;
  courseTypeDescription?: string;
  trainings: TrainingRecord[];
  downloadState?: boolean;
  downloadEnabled?: boolean;
  onTrainingSelected: (item: TrainingRecord) => void;
  onTrainingRetryDownloadClick?: (item: TrainingRecord) => void;
  onTrainingDownloadClick?: (item: TrainingRecord) => void;
  onTrainingDeleteClick?: (item: TrainingRecord) => void;
}
const trainingType = [
  {
    id: 1,
    name: 'All',
  },
  {
    id: 2,
    name: 'Downloads',
  },
];
export const CourseTrainings: FC<CourseTrainingsProps> = ({
  course,
  trainings,
  courseCategoryName,
  courseTypeDescription,
  downloadState = false,
  downloadEnabled = false,
  onTrainingSelected,
  onTrainingDownloadClick,
  onTrainingRetryDownloadClick,
  onTrainingDeleteClick,
}) => {
  const [selectedtraining, setSelectedtraining] = useState(1);
  const [selectedTrainingList, setSelectedTrainingList] =
    useState<ITraining[]>(trainings);
  useEffect(() => {
    const trainingList =
      selectedtraining === 2
        ? [...trainings]
            .filter(
              (training) =>
                training.mediaAsset?.download?.status ===
                DownloadStatus.Completed,
            )
            .sort((a, b) =>
              a.mediaAsset?.download?.status === DownloadStatus.Completed &&
              b.mediaAsset?.download?.status !== DownloadStatus.Completed
                ? -1
                : b.mediaAsset?.download?.status === DownloadStatus.Completed &&
                    a.mediaAsset?.download?.status !== DownloadStatus.Completed
                  ? 1
                  : 0,
            )
        : trainings;
    setSelectedTrainingList(trainingList);
  }, [selectedtraining, trainings]);
  return (
    <>
      <Typography
        data-cy="teacher-training-course-title"
        variant="textHeadlineSmallSurface"
      >
        {courseCategoryName}
      </Typography>

      <Typography
        data-cy="teacher-training-course-title"
        variant="textBodyMediumNeutral10"
      >
        {courseTypeDescription}
      </Typography>
      {course.gradeGroup === null && (
        <CourseDetailContainer>
          <Typography
            data-cy="teacher-training-course-subtitle"
            variant="textTitleMediumNeutral10"
          >
            About this Course
          </Typography>
          <Typography
            data-cy="teacher-training-course-description"
            variant="textBodyLargeNeutral50"
          >
            {course.description}
          </Typography>
          <Typography variant="textTitleMediumNeutral10">
            Skills you will gain
          </Typography>
          <StyledListBox>
            {course?.keywords?.map((keyword: string) => (
              <ChipAssist key={keyword} label={keyword} />
            ))}
          </StyledListBox>
        </CourseDetailContainer>
      )}
      {Capacitor.isNativePlatform() && downloadEnabled && (
        <StyledDiv
          gap={10}
          marginTop={16}
          margin="var(--rk-spacing-xl) 0 var(--rk-spacing-xl) 0"
        >
          {trainingType.map((item) => (
            <ChipFilterWithContainer
              selected={selectedtraining === item.id}
              onClick={() => {
                setSelectedtraining(item.id);
              }}
              key={item.name}
              label={item.name}
            />
          ))}
        </StyledDiv>
      )}
      <Spacing marginTop="xxl" marginBottom="lg">
        <TrainingHeader>
          <IconText
            icon="play_circle"
            iconColor="--md-ref-primary-40"
            text="Trainings"
            textVariant="textTitleLargePrimary40"
            data-cy="teacher-training-trainings-title"
          ></IconText>
        </TrainingHeader>
      </Spacing>
      <TrainingCardList
        trainingDownloadEnabled={downloadEnabled}
        trainings={selectedTrainingList}
        downloadUIState={downloadState}
        onTrainingCardClick={onTrainingSelected}
        onTrainingDeleteClick={(data) => onTrainingDeleteClick?.(data)}
        onTrainingDownloadClick={(data) => onTrainingDownloadClick?.(data)}
        onTrainingRetryDownloadClick={(data) =>
          onTrainingRetryDownloadClick?.(data)
        }
      />
      {selectedTrainingList.length === 0 &&
        selectedtraining === 2 &&
        downloadEnabled && (
          <StyledNoDownloadLp>
            <Typography variant="textTitleMediumNeutral10">
              Your downloaded trainings <br />
              will appear here
            </Typography>
          </StyledNoDownloadLp>
        )}
    </>
  );
};
const StyledNoDownloadLp = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 240px);
  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 455px);
  }
`;

const CourseDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-md);
  margin-top: var(--rk-spacing-xl);
`;

const StyledListBox = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--rk-spacing-md);
`;

const TrainingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
